import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Logo from '../components/Logo'
import { useAuth } from '../contexts/AuthContext'
import {
  getProfile, updateProfile,
  getConnections, upsertConnection, testConnection,
  backfillVersions,
  getMCPApiKey, generateMCPApiKey, testMCPConnection,
  getPreferences, updatePreferences,
  getMCPOAuth, generateMCPOAuth,
  getOAuthClient, createOAuthClient,
  deleteConnection,
  listOrgs, createOrg, listOrgMembers, listOrgInvitations,
  inviteOrgMember, cancelOrgInvitation, removeOrgMember, getRemovalImpact,
} from '../api'
import { memberName, memberInitials, isPendingMember } from '../lib/memberName'

// ─── Types ───────────────────────────────────────────────────────────────────

interface Profile { display_name: string; workspace_name: string }
interface GHConfig { repo: string; branch: string; token: string }
interface OWConfig { url: string; api_key: string }
interface ClaudeConfig { api_key: string }

type TestState = 'idle' | 'testing' | 'ok' | 'fail'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-[#1e1e1e] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[10px] overflow-hidden mb-4">
      <div className="px-6 py-4 border-b border-[#f0f0ef] dark:border-[#2e2e2e]">
        <h2 className="text-[13px] font-semibold text-[#1a1a1a] dark:text-[#f0f0ef]">{title}</h2>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <label className="block text-[11px] font-medium text-[#64748b] dark:text-[#555] uppercase tracking-[0.5px] mb-1.5">
        {label}
      </label>
      {children}
    </div>
  )
}

function Input({ value, onChange, placeholder, type = 'text', readOnly = false }: {
  value: string; onChange?: (v: string) => void
  placeholder?: string; type?: string; readOnly?: boolean
}) {
  return (
    <input
      type={type}
      value={value}
      readOnly={readOnly}
      onChange={e => onChange?.(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-2.5 py-[7px] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[13px] dark:text-[#f0f0ef] outline-none focus:border-[#bbb] dark:focus:border-[#555] ${
        readOnly
          ? 'bg-[#f5f5f4] dark:bg-[#252525] text-[#64748b] dark:text-[#555] cursor-default'
          : 'bg-white dark:bg-[#222] placeholder:text-[#94a3b8] dark:placeholder:text-[#444]'
      }`}
    />
  )
}

function SaveBtn({ loading, saved, onClick }: { loading: boolean; saved: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="px-4 py-1.5 bg-[#1a1a1a] dark:bg-[#f0f0ef] text-white dark:text-[#1a1a1a] rounded-[6px] text-[12px] font-medium hover:bg-[#333] dark:hover:bg-[#ddd] disabled:opacity-50 cursor-pointer"
    >
      {loading ? 'Saving…' : saved ? '✓ Saved' : 'Save'}
    </button>
  )
}

function TestBtn({ state, onClick }: { state: TestState; onClick: () => void }) {
  const label = state === 'testing' ? 'Testing…' : state === 'ok' ? '✓ Connected' : state === 'fail' ? '✗ Failed' : 'Test connection'
  const cls = state === 'ok'
    ? 'text-[#16a34a] border-[#bbf7d0] bg-[#f0fdf4] dark:bg-[#0f2a1a] dark:border-[#166534] dark:text-[#4ade80]'
    : state === 'fail'
    ? 'text-[#e53e3e] border-[#fecaca] bg-[#fff5f5] dark:bg-[#2a1010] dark:border-[#991b1b] dark:text-[#f87171]'
    : 'text-[#475569] dark:text-[#aaa] border-[#e5e5e3] dark:border-[#2e2e2e] hover:border-[#bbb] dark:hover:border-[#555] bg-transparent'
  return (
    <button
      onClick={onClick}
      disabled={state === 'testing'}
      className={`px-3 py-1.5 border rounded-[6px] text-[12px] font-medium cursor-pointer disabled:opacity-50 ${cls}`}
    >
      {label}
    </button>
  )
}

function ComingSoon() {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#fffbeb] text-[#d97706] border border-[#fde68a]">
      Coming soon
    </span>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const [toast, setToast] = useState('')
  type SettingsTab = 'profile' | 'preferences' | 'notifications' | 'github' | 'claude' | 'platforms' | 'organizations' | 'billing' | 'advanced'
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile')

  // Profile
  const [profile, setProfile] = useState<Profile>({ display_name: '', workspace_name: '' })
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileSaved, setProfileSaved] = useState(false)

  // GitHub
  const [gh, setGh] = useState<GHConfig>({ repo: '', branch: 'main', token: '' })
  const [ghLoading, setGhLoading] = useState(false)
  const [ghSaved, setGhSaved] = useState(false)
  const [ghTest, setGhTest] = useState<TestState>('idle')
  const [ghTestMsg, setGhTestMsg] = useState('')
  const [showGhDisconnect, setShowGhDisconnect] = useState(false)

  // Open WebUI
  const [ow, setOw] = useState<OWConfig>({ url: '', api_key: '' })
  const [owLoading, setOwLoading] = useState(false)
  const [owSaved, setOwSaved] = useState(false)
  const [owTest, setOwTest] = useState<TestState>('idle')

  // Claude
  const [cl, setCl] = useState<ClaudeConfig>({ api_key: '' })
  const [clLoading, setClLoading] = useState(false)
  const [clSaved, setClSaved] = useState(false)

  // Backfill
  type BackfillState = 'idle' | 'running' | 'done' | 'error'
  const [backfillState, setBackfillState] = useState<BackfillState>('idle')
  const [backfillResult, setBackfillResult] = useState<{ inserted: number; skipped: number; errors: string[] } | null>(null)

  // MCP API Key
  const [mcpKey, setMcpKey] = useState<string | null>(null)
  const [mcpKeyFull, setMcpKeyFull] = useState<string | null>(null)
  const [mcpGenerating, setMcpGenerating] = useState(false)
  const [mcpCopied, setMcpCopied] = useState(false)
  const [mcpConfigCopied, setMcpConfigCopied] = useState(false)
  const [mcpTestState, setMcpTestState] = useState<'idle' | 'testing' | 'ok' | 'fail'>('idle')

  // OAuth
  const [oauthClientId, setOauthClientId] = useState<string | null>(null)
  const [oauthSecret, setOauthSecret] = useState<string | null>(null)
  const [oauthSecretFull, setOauthSecretFull] = useState<string | null>(null)
  const [oauthGenerating, setOauthGenerating] = useState(false)
  const [oauthIdCopied, setOauthIdCopied] = useState(false)
  const [oauthSecretCopied, setOauthSecretCopied] = useState(false)
  const [oauthSecretRevealed, setOauthSecretRevealed] = useState(false)
  const [authUrlCopied, setAuthUrlCopied] = useState(false)
  const [tokenUrlCopied, setTokenUrlCopied] = useState(false)

  const [toastType, setToastType] = useState<'ok' | 'error'>('ok')

  // Organizations
  interface OrgInfo { id: string; name: string; slug: string; owner_id: string; your_role: string; member_count: number }
  interface OrgMember { id?: string; user_id: string; display_name?: string; email?: string; role: string }
  interface OrgInvite { id: string; email: string; role: string; created_at: string }
  const [orgs, setOrgs] = useState<OrgInfo[]>([])
  const [orgsLoading, setOrgsLoading] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<OrgInfo | null>(null)
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([])
  const [orgInvitations, setOrgInvitations] = useState<OrgInvite[]>([])
  const [orgDetailLoading, setOrgDetailLoading] = useState(false)
  const [showNewOrg, setShowNewOrg] = useState(false)
  const [newOrgName, setNewOrgName] = useState('')
  const [newOrgSlug, setNewOrgSlug] = useState('')
  const [newOrgSaving, setNewOrgSaving] = useState(false)
  // Invite (wired in step 7)
  const [showOrgInvite, setShowOrgInvite] = useState(false)
  const [invOrgEmail, setInvOrgEmail] = useState('')
  const [invOrgRole, setInvOrgRole] = useState('member')
  const [invOrgSaving, setInvOrgSaving] = useState(false)
  // Remove confirm
  const [removeConfirmMember, setRemoveConfirmMember] = useState<OrgMember | null>(null)
  const [removeImpactCount, setRemoveImpactCount] = useState<number | null>(null)
  const [removeImpactLoading, setRemoveImpactLoading] = useState(false)

  const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

  const loadOrgs = async () => {
    setOrgsLoading(true)
    try {
      const r = await listOrgs()
      setOrgs(Array.isArray(r.data) ? r.data : [])
    } catch {} finally { setOrgsLoading(false) }
  }

  const loadOrgDetail = async (org: OrgInfo) => {
    setSelectedOrg(org)
    setOrgDetailLoading(true)
    try {
      const [mr, ir] = await Promise.all([listOrgMembers(org.id), listOrgInvitations(org.id)])
      setOrgMembers(Array.isArray(mr.data) ? mr.data : [])
      setOrgInvitations(Array.isArray(ir.data) ? ir.data : [])
    } catch {} finally { setOrgDetailLoading(false) }
  }

  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) return
    setNewOrgSaving(true)
    try {
      const r = await createOrg({ name: newOrgName.trim(), slug: newOrgSlug || slugify(newOrgName.trim()) })
      showToast('✓ Organization created')
      setShowNewOrg(false); setNewOrgName(''); setNewOrgSlug('')
      await loadOrgs()
      loadOrgDetail(r.data)
    } catch (e: any) {
      showToast(e?.response?.data?.detail || '✗ Failed to create', 'error')
    } finally { setNewOrgSaving(false) }
  }

  const handleInviteOrgMember = async () => {
    if (!selectedOrg || !invOrgEmail.trim()) return
    setInvOrgSaving(true)
    try {
      await inviteOrgMember(selectedOrg.id, { email: invOrgEmail.trim(), role: invOrgRole })
      showToast(`✓ Invite sent to ${invOrgEmail}`)
      setShowOrgInvite(false); setInvOrgEmail(''); setInvOrgRole('member')
      loadOrgDetail(selectedOrg)
    } catch (e: any) {
      showToast(e?.response?.data?.detail || '✗ Failed to invite', 'error')
    } finally { setInvOrgSaving(false) }
  }

  const handleCancelInvite = async (inv: OrgInvite) => {
    if (!selectedOrg) return
    try {
      await cancelOrgInvitation(selectedOrg.id, inv.id)
      setOrgInvitations(prev => prev.filter(i => i.id !== inv.id))
      showToast('✓ Invite cancelled')
    } catch { showToast('✗ Failed to cancel', 'error') }
  }

  const handleRemoveMember = async (m: OrgMember) => {
    if (!selectedOrg) return
    try {
      await removeOrgMember(selectedOrg.id, m.user_id)
      setOrgMembers(prev => prev.filter(x => x.user_id !== m.user_id))
      setRemoveConfirmMember(null)
      setRemoveImpactCount(null)
      showToast('✓ Member removed')
    } catch (e: any) {
      const detail = e?.response?.data?.detail || '✗ Failed to remove'
      showToast(detail, 'error')
      setRemoveConfirmMember(null)
      setRemoveImpactCount(null)
    }
  }

  const showToast = (msg: string, type: 'ok' | 'error' = 'ok') => {
    setToast(msg)
    setToastType(type)
    clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(''), 2000)
  }

  // Debounce refs for auto-save
  const profileDebounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const ghDebounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const owDebounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const clDebounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const initialLoad = useRef(true)

  // Preferences
  const [prefs, setPrefs] = useState<Record<string, boolean>>({
    dark_mode: true, compact_view: false, show_checklist: true,
    ai_suggestions: true, auto_save: true,
    notif_published: true, notif_comments: true, notif_conflicts: true,
    whats_new_muted: false,
  })

  useEffect(() => {
    // Load profile
    getProfile().then(r => {
      setProfile({ display_name: r.data.display_name || '', workspace_name: r.data.workspace_name || '' })
    }).catch(() => {})

    // Load connections
    getConnections().then(r => {
      const rows: { type: string; config: Record<string, string> }[] = r.data || []
      rows.forEach(row => {
        if (row.type === 'github') setGh({ repo: row.config.repo || '', branch: row.config.branch || 'main', token: row.config.token || '' })
        if (row.type === 'openwebui') setOw({ url: row.config.url || '', api_key: row.config.api_key || '' })
        if (row.type === 'claude') setCl({ api_key: row.config.api_key || '' })
      })
    }).catch(() => {})

    // Load MCP API key
    getMCPApiKey().then(r => {
      if (r.data.exists) setMcpKey(r.data.key)
    }).catch(() => {})

    // Load OAuth credentials — auto-generate if none exist
    getOAuthClient().then(r => {
      if (r.data.exists) {
        setOauthClientId(r.data.client_id)
        setOauthSecret(r.data.client_secret)
      } else {
        // Auto-generate on first visit
        createOAuthClient().then(cr => {
          setOauthClientId(cr.data.client_id)
          setOauthSecret(cr.data.client_secret)
          setOauthSecretFull(cr.data.client_secret)
        }).catch(() => {})
      }
    }).catch(() => {})

    // Load preferences
    getPreferences().then(r => {
      if (r.data && typeof r.data === 'object') setPrefs(p => ({ ...p, ...r.data }))
    }).catch(() => {})

    // Load orgs
    loadOrgs()

    // Mark initial load done after a tick so auto-save effects don't fire on load
    setTimeout(() => { initialLoad.current = false }, 500)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle and persist a preference
  const togglePref = async (key: string) => {
    const newVal = !prefs[key]
    setPrefs(p => ({ ...p, [key]: newVal }))
    try {
      await updatePreferences({ [key]: newVal })
      showToast('✓ Saved')
    } catch {
      showToast('✗ Failed to save — try again', 'error')
      setPrefs(p => ({ ...p, [key]: !newVal })) // revert
    }
  }

  // ── Auto-save: Profile (debounced 1s)
  useEffect(() => {
    if (initialLoad.current) return
    if (!profile.display_name && !profile.workspace_name) return
    clearTimeout(profileDebounce.current)
    profileDebounce.current = setTimeout(async () => {
      try {
        await updateProfile(profile)
        showToast('✓ Saved')
      } catch {
        showToast('✗ Failed to save — try again', 'error')
      }
    }, 1000)
    return () => clearTimeout(profileDebounce.current)
  }, [profile]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-save: GitHub (debounced 1.5s)
  useEffect(() => {
    if (initialLoad.current) return
    if (!gh.repo && !gh.token) return
    clearTimeout(ghDebounce.current)
    ghDebounce.current = setTimeout(async () => {
      try {
        await upsertConnection('github', gh)
        localStorage.setItem('skilflow_github', JSON.stringify(gh))
        showToast('✓ Saved')
      } catch {
        showToast('✗ Failed to save — try again', 'error')
      }
    }, 1500)
    return () => clearTimeout(ghDebounce.current)
  }, [gh]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── GitHub test
  const testGitHub = async () => {
    setGhTest('testing')
    setGhTestMsg('')
    try {
      const r = await testConnection('github', gh)
      setGhTest('ok')
      setGhTestMsg(`Connected to ${r.data.repo}`)
    } catch (e: any) {
      setGhTest('fail')
      setGhTestMsg(e?.response?.data?.detail || 'Connection failed')
    }
    setTimeout(() => setGhTest('idle'), 4000)
  }

  // ── Auto-save: Open WebUI (debounced 1.5s)
  useEffect(() => {
    if (initialLoad.current) return
    if (!ow.url && !ow.api_key) return
    clearTimeout(owDebounce.current)
    owDebounce.current = setTimeout(async () => {
      try {
        await upsertConnection('openwebui', ow)
        showToast('✓ Saved')
      } catch {
        showToast('✗ Failed to save — try again', 'error')
      }
    }, 1500)
    return () => clearTimeout(owDebounce.current)
  }, [ow]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Open WebUI test
  const testOpenWebUI = async () => {
    setOwTest('testing')
    try {
      await testConnection('openwebui', ow)
      setOwTest('ok')
    } catch {
      setOwTest('fail')
    }
    setTimeout(() => setOwTest('idle'), 4000)
  }

  // ── Auto-save: Claude (debounced 1.5s)
  useEffect(() => {
    if (initialLoad.current) return
    if (!cl.api_key) return
    clearTimeout(clDebounce.current)
    clDebounce.current = setTimeout(async () => {
      try {
        await upsertConnection('claude', cl)
        showToast('✓ Saved')
      } catch {
        showToast('✗ Failed to save — try again', 'error')
      }
    }, 1500)
    return () => clearTimeout(clDebounce.current)
  }, [cl]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: 'var(--bg)', color: 'var(--text1)' }}>
      {/* Nav */}
      <nav className="h-[52px] flex items-center px-5 gap-3 flex-shrink-0 sticky top-0 z-10" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <Logo linkTo="/" />
        <button
          onClick={() => navigate('/')}
          className="text-[13px] text-[#64748b] dark:text-[#555] hover:text-[#475569] dark:hover:text-[#aaa] cursor-pointer bg-none border-none p-0 flex items-center gap-1"
        >
          ← Skills
        </button>
        <div className="ml-auto flex items-center gap-2.5">
          <div className="relative">
            <div
              onClick={() => setShowUserMenu(m => !m)}
              className="w-[30px] h-[30px] rounded-full bg-[#7c3aed] flex items-center justify-center text-[12px] font-semibold text-white cursor-pointer flex-shrink-0 select-none"
            >
              {user?.email?.[0]?.toUpperCase() ?? 'U'}
            </div>
            {showUserMenu && (
              <div className="absolute right-0 top-[38px] w-[200px] bg-white dark:bg-[#1e1e1e] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[8px] shadow-lg z-50 py-1">
                <div className="px-3 py-2 text-[11px] text-[#64748b] dark:text-[#555] truncate border-b border-[#f0f0ef] dark:border-[#2e2e2e] mb-1">{user?.email}</div>
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/settings') }}
                  className="w-full text-left px-3 py-1.5 text-[13px] text-[#1a1a1a] dark:text-[#f0f0ef] hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer"
                >
                  Settings
                </button>
                <button
                  onClick={async () => { setShowUserMenu(false); await signOut(); navigate('/login') }}
                  className="w-full text-left px-3 py-1.5 text-[13px] text-[#e53e3e] hover:bg-[#fff5f5] dark:hover:bg-[#2a1a1a] cursor-pointer"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Settings layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="w-[220px] flex-shrink-0 p-[20px_12px] overflow-y-auto" style={{ borderRight: '1px solid var(--border)' }}>
          <NavSection label="Account">
            <NavItem tab="profile" active={activeTab} onClick={setActiveTab}
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}>Profile</NavItem>
            <NavItem tab="preferences" active={activeTab} onClick={setActiveTab}
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93l-1.41 1.41M5.34 18.66l-1.41 1.41M20 12h-2M6 12H4M18.66 18.66l-1.41-1.41M6.76 6.76L5.34 5.34"/></svg>}>Preferences</NavItem>
            <NavItem tab="notifications" active={activeTab} onClick={setActiveTab}
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>}>Notifications</NavItem>
          </NavSection>
          <NavSection label="Integrations">
            <NavItem tab="github" active={activeTab} onClick={setActiveTab}
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/></svg>}>GitHub</NavItem>
            <NavItem tab="claude" active={activeTab} onClick={setActiveTab}
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>}>Claude Desktop</NavItem>
            <NavItem tab="platforms" active={activeTab} onClick={setActiveTab}
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>}>Platforms</NavItem>
          </NavSection>
          <NavSection label="Workspace">
            <NavItem tab="organizations" active={activeTab} onClick={(t) => { setActiveTab(t); if (t === 'organizations') loadOrgs() }}
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}>Organizations</NavItem>
            <NavItem tab="billing" active={activeTab} onClick={setActiveTab}
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>}>Billing</NavItem>
          </NavSection>
          <NavSection label="Danger zone">
            <NavItem tab="advanced" active={activeTab} onClick={setActiveTab} danger
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}>Advanced</NavItem>
          </NavSection>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-[32px_40px] max-w-[680px]">

        {/* ── Profile ── */}
        {/* Page title */}
        <div className="text-[20px] font-bold tracking-[-0.4px] mb-1" style={{ color: 'var(--text1)' }}>
          {{profile:'Profile',preferences:'Preferences',notifications:'Notifications',github:'GitHub',claude:'Claude Desktop',platforms:'Platforms',organizations:'Organizations',billing:'Billing',advanced:'Advanced'}[activeTab]}
        </div>
        <div className="text-[13px] mb-8" style={{ color: 'var(--text2)' }}>
          {{profile:'Manage your account details.',preferences:'Customize how Skilflow looks and behaves.',notifications:'Manage email and in-app notifications.',github:'Connect your skill repository.',claude:'Connect Skilflow to Claude via MCP.',platforms:'Manage connected AI platforms.',organizations:'Create organizations and manage members.',billing:'Manage your plan and payment.',advanced:'Dangerous actions — proceed with care.'}[activeTab]}
        </div>

        {activeTab === 'profile' && <Section title="Profile">
          <Field label="Display name">
            <Input
              value={profile.display_name}
              onChange={v => setProfile(p => ({ ...p, display_name: v }))}
              placeholder="David Oliver"
            />
          </Field>
          <Field label="Email">
            <Input value={user?.email || ''} readOnly />
          </Field>
          <Field label="Workspace name">
            <Input
              value={profile.workspace_name}
              onChange={v => setProfile(p => ({ ...p, workspace_name: v }))}
              placeholder="Mitel Marketing"
            />
          </Field>
        </Section>}

        {activeTab === 'github' && <Section title="Connected Sources">
          {/* GitHub */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-[6px] bg-[#1a1a1a] dark:bg-[#f0f0ef] flex items-center justify-center flex-shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white" className="dark:fill-[#1a1a1a]">
                  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
                </svg>
              </div>
              <span className="text-[13px] font-medium text-[#1a1a1a] dark:text-[#f0f0ef]">GitHub</span>
            </div>
            <Field label="Repository (owner/repo)">
              <Input
                value={gh.repo}
                onChange={v => { setGh(g => ({ ...g, repo: v })); setGhTest('idle') }}
                placeholder="your-org/skills-repo"
              />
            </Field>
            <Field label="Branch">
              <Input
                value={gh.branch}
                onChange={v => { setGh(g => ({ ...g, branch: v })); setGhTest('idle') }}
                placeholder="main"
              />
            </Field>
            <Field label="Personal access token">
              <Input
                type="password"
                value={gh.token}
                onChange={v => { setGh(g => ({ ...g, token: v })); setGhTest('idle') }}
                placeholder="ghp_…"
              />
              <p className="text-[11px] text-[#64748b] dark:text-[#444] mt-1">Needs Contents read &amp; write access.</p>
            </Field>
            {ghTestMsg && (
              <p className={`text-[12px] mb-3 ${ghTest === 'ok' ? 'text-[#16a34a]' : 'text-[#e53e3e]'}`}>{ghTestMsg}</p>
            )}
            <div className="flex items-center gap-3">
              <TestBtn state={ghTest} onClick={testGitHub} />
              {gh.repo && (
                <button
                  onClick={() => setShowGhDisconnect(true)}
                  className="text-[12px] font-medium cursor-pointer bg-transparent border-none"
                  style={{ color: '#ef4444' }}>
                  Disconnect
                </button>
              )}
            </div>
          </div>

          <div className="h-px bg-[#f0f0ef] dark:bg-[#2e2e2e] mb-5" />

          {/* Google Drive */}
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-[6px] bg-[#f5f5f4] dark:bg-[#252525] border border-[#e5e5e3] dark:border-[#2e2e2e] flex items-center justify-center flex-shrink-0">
              <svg width="14" height="14" viewBox="0 0 87.3 78" fill="none">
                <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3L27.5 53H0c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                <path d="M43.65 25L29.9 0c-1.35.8-2.5 1.9-3.3 3.3L1.2 48.5A9 9 0 000 53h27.5z" fill="#00ac47"/>
                <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l5.85 11.5z" fill="#ea4335"/>
                <path d="M43.65 25L57.4 0H29.9z" fill="#00832d"/>
                <path d="M59.8 53H87.3l-13.75-23.8L57.4 0 43.65 25 59.8 53z" fill="#2684fc"/>
                <path d="M27.5 53l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h51.05c1.6 0 3.15-.45 4.5-1.2L59.8 53H27.5z" fill="#ffba00"/>
              </svg>
            </div>
            <span className="text-[13px] font-medium text-[#1a1a1a] dark:text-[#f0f0ef]">Google Drive</span>
            <ComingSoon />
          </div>
        </Section>}

        {activeTab === 'platforms' && <Section title="Connected Destinations">
          {/* Open WebUI */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-[6px] bg-[#eff4ff] flex items-center justify-center flex-shrink-0">
                <span className="text-[12px] font-bold text-[#2563eb]">W</span>
              </div>
              <span className="text-[13px] font-medium text-[#1a1a1a] dark:text-[#f0f0ef]">Open WebUI</span>
            </div>
            <Field label="Instance URL">
              <Input
                value={ow.url}
                onChange={v => { setOw(o => ({ ...o, url: v })); setOwTest('idle') }}
                placeholder="https://your-openwebui.com"
              />
            </Field>
            <Field label="API key">
              <Input
                type="password"
                value={ow.api_key}
                onChange={v => { setOw(o => ({ ...o, api_key: v })); setOwTest('idle') }}
                placeholder="sk-…"
              />
            </Field>
            <div className="flex items-center">
              <TestBtn state={owTest} onClick={testOpenWebUI} />
            </div>
          </div>

          <div className="h-px bg-[#f0f0ef] dark:bg-[#2e2e2e] mb-5" />

          {/* Claude */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-[6px] bg-[#f5f0ff] flex items-center justify-center flex-shrink-0">
                <span className="text-[12px] font-bold text-[#7c3aed]">C</span>
              </div>
              <span className="text-[13px] font-medium text-[#1a1a1a] dark:text-[#f0f0ef]">Claude API</span>
            </div>
            <Field label="API key">
              <Input
                type="password"
                value={cl.api_key}
                onChange={v => setCl({ api_key: v })}
                placeholder="sk-ant-…"
              />
              <p className="text-[11px] text-[#64748b] dark:text-[#444] mt-1">Used for publishing skills to Claude projects.</p>
            </Field>
          </div>
        </Section>}

        {activeTab === 'github' && <Section title="Data &amp; Tools">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[13px] font-medium text-[#1a1a1a] dark:text-[#f0f0ef] mb-1">Backfill version history</div>
              <p className="text-[12px] text-[#64748b] dark:text-[#666] leading-relaxed max-w-[420px]">
                Scans your GitHub repo for versioned archive files (e.g. <code className="bg-[#f5f5f4] dark:bg-[#252525] px-1 rounded text-[11px]">mitel-tone-v2.2.md</code>) and imports them as version history entries. Safe to run multiple times — skips existing entries.
              </p>
              {backfillState === 'done' && backfillResult && (
                <div className="mt-2 text-[12px] text-[#16a34a]">
                  ✓ {backfillResult.inserted} version{backfillResult.inserted !== 1 ? 's' : ''} backfilled
                  {backfillResult.skipped > 0 && <span className="text-[#64748b] ml-2">· {backfillResult.skipped} already existed</span>}
                  {backfillResult.errors.length > 0 && <span className="text-[#e53e3e] ml-2">· {backfillResult.errors.length} errors</span>}
                </div>
              )}
              {backfillState === 'error' && (
                <div className="mt-2 text-[12px] text-[#e53e3e]">✗ Backfill failed — check GitHub connection in Connected Sources</div>
              )}
            </div>
            <button
              onClick={async () => {
                setBackfillState('running')
                setBackfillResult(null)
                try {
                  const r = await backfillVersions()
                  setBackfillResult(r.data)
                  setBackfillState('done')
                } catch {
                  setBackfillState('error')
                }
              }}
              disabled={backfillState === 'running'}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-1.5 border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[12px] font-medium text-[#475569] dark:text-[#aaa] hover:border-[#bbb] dark:hover:border-[#555] hover:bg-[#f5f5f4] dark:hover:bg-[#252525] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {backfillState === 'running' && (
                <svg className="animate-spin w-3.5 h-3.5 text-[#475569]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                </svg>
              )}
              {backfillState === 'running' ? 'Running…' : 'Run backfill'}
            </button>
          </div>
        </Section>}

        {activeTab === 'claude' && <Section title="Connect to Claude Desktop">
          <p className="text-[12px] text-[#64748b] dark:text-[#666] leading-relaxed mb-3">
            Connect Skilflow to Claude Desktop so you can manage skills directly from conversations —
            say things like "list my skills" or "save this as a skill called my-tone".
          </p>
          <a href="/connect" className="text-[12px] font-medium mb-5 inline-block" style={{ color: 'var(--brand-light)' }}>
            Connect to Claude.ai — step by step guide &rarr;
          </a>

          {/* Step 1 — OAuth Credentials */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-[22px] h-[22px] rounded-full bg-[#1a1a1a] dark:bg-[#f0f0ef] flex items-center justify-center text-[11px] font-bold text-white dark:text-[#1a1a1a] flex-shrink-0">1</div>
              <span className="text-[13px] font-medium text-[#1a1a1a] dark:text-[#f0f0ef]">OAuth credentials</span>
            </div>

            <Field label="Client ID">
              <div className="flex gap-2">
                <div className="flex-1 px-2.5 py-[7px] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[13px] bg-[#f5f5f4] dark:bg-[#252525] text-[#475569] dark:text-[#aaa] font-mono overflow-hidden text-ellipsis whitespace-nowrap select-all">
                  {oauthClientId || 'Loading…'}
                </div>
                {oauthClientId && (
                  <button onClick={() => { navigator.clipboard.writeText(oauthClientId); setOauthIdCopied(true); setTimeout(() => setOauthIdCopied(false), 2000) }}
                    className="px-3 py-1.5 border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[12px] font-medium text-[#475569] dark:text-[#aaa] bg-transparent hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer flex-shrink-0"
                  >{oauthIdCopied ? '✓ Copied' : 'Copy'}</button>
                )}
              </div>
            </Field>

            <Field label="Client Secret">
              <div className="flex gap-2">
                <div className="flex-1 px-2.5 py-[7px] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[13px] bg-[#f5f5f4] dark:bg-[#252525] text-[#475569] dark:text-[#aaa] font-mono overflow-hidden text-ellipsis whitespace-nowrap select-all">
                  {oauthSecretRevealed
                    ? (oauthSecretFull || oauthSecret || 'Loading…')
                    : (oauthSecret ? '••••••••••••••••••••••••' : 'Loading…')}
                </div>
                {oauthSecret && !oauthSecretRevealed && (
                  <button onClick={() => setOauthSecretRevealed(true)}
                    className="px-3 py-1.5 border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[12px] font-medium text-[#475569] dark:text-[#aaa] bg-transparent hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer flex-shrink-0"
                  >Reveal</button>
                )}
                {oauthSecret && oauthSecretRevealed && (
                  <button onClick={() => { navigator.clipboard.writeText(oauthSecretFull || oauthSecret || ''); setOauthSecretCopied(true); setTimeout(() => setOauthSecretCopied(false), 2000) }}
                    className="px-3 py-1.5 border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[12px] font-medium text-[#475569] dark:text-[#aaa] bg-transparent hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer flex-shrink-0"
                  >{oauthSecretCopied ? '✓ Copied' : 'Copy'}</button>
                )}
              </div>
            </Field>

            <Field label="API Key (for config file)">
              <div className="flex gap-2">
                <div className="flex-1 px-2.5 py-[7px] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[13px] bg-[#f5f5f4] dark:bg-[#252525] text-[#475569] dark:text-[#aaa] font-mono overflow-hidden text-ellipsis whitespace-nowrap select-all">
                  {mcpKey || 'Loading…'}
                </div>
                {mcpKey && (
                  <button onClick={() => { navigator.clipboard.writeText(mcpKey); setMcpCopied(true); setTimeout(() => setMcpCopied(false), 2000) }}
                    className="px-3 py-1.5 border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[12px] font-medium text-[#475569] dark:text-[#aaa] bg-transparent hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer flex-shrink-0"
                  >{mcpCopied ? '✓ Copied' : 'Copy'}</button>
                )}
              </div>
            </Field>

            <div className="h-px bg-[#f0f0ef] dark:bg-[#2e2e2e] my-4" />

            <Field label="Authorization URL">
              <div className="flex gap-2">
                <div className="flex-1 px-2.5 py-[7px] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[12px] bg-[#f5f5f4] dark:bg-[#252525] text-[#475569] dark:text-[#aaa] font-mono select-all">
                  https://mcp.skilflow.ai/oauth/authorize
                </div>
                <button onClick={() => { navigator.clipboard.writeText('https://mcp.skilflow.ai/oauth/authorize'); setAuthUrlCopied(true); setTimeout(() => setAuthUrlCopied(false), 2000) }}
                  className="px-3 py-1.5 border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[12px] font-medium text-[#475569] dark:text-[#aaa] bg-transparent hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer flex-shrink-0"
                >{authUrlCopied ? '✓ Copied' : 'Copy'}</button>
              </div>
            </Field>

            <Field label="Token URL">
              <div className="flex gap-2">
                <div className="flex-1 px-2.5 py-[7px] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[12px] bg-[#f5f5f4] dark:bg-[#252525] text-[#475569] dark:text-[#aaa] font-mono select-all">
                  https://mcp.skilflow.ai/oauth/token
                </div>
                <button onClick={() => { navigator.clipboard.writeText('https://mcp.skilflow.ai/oauth/token'); setTokenUrlCopied(true); setTimeout(() => setTokenUrlCopied(false), 2000) }}
                  className="px-3 py-1.5 border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[12px] font-medium text-[#475569] dark:text-[#aaa] bg-transparent hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer flex-shrink-0"
                >{tokenUrlCopied ? '✓ Copied' : 'Copy'}</button>
              </div>
            </Field>

            <div className="h-px bg-[#f0f0ef] dark:bg-[#2e2e2e] my-4" />

            <button
              onClick={async () => {
                if (!window.confirm('Regenerating will invalidate your current credentials. Any existing Claude Desktop connections will stop working. Continue?')) return
                setOauthGenerating(true)
                try {
                  const r = await createOAuthClient()
                  setOauthClientId(r.data.client_id)
                  setOauthSecret(r.data.client_secret)
                  setOauthSecretFull(r.data.client_secret)
                  setOauthSecretRevealed(true)
                  showToast('New credentials generated')
                } catch { showToast('Failed to generate credentials') }
                finally { setOauthGenerating(false) }
              }}
              disabled={oauthGenerating}
              className="px-3 py-1.5 border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[12px] font-medium text-[#475569] dark:text-[#aaa] bg-transparent hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer disabled:opacity-50"
            >
              {oauthGenerating ? 'Generating…' : 'Regenerate credentials'}
            </button>
            <p className="text-[11px] text-[#64748b] dark:text-[#444] mt-1.5">Regenerating will invalidate your current Client ID and Secret.</p>
          </div>

          <div className="h-px bg-[#f0f0ef] dark:bg-[#2e2e2e] mb-5" />

          {/* Step 2 — Setup */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-[22px] h-[22px] rounded-full bg-[#1a1a1a] dark:bg-[#f0f0ef] flex items-center justify-center text-[11px] font-bold text-white dark:text-[#1a1a1a] flex-shrink-0">2</div>
              <span className="text-[13px] font-medium text-[#1a1a1a] dark:text-[#f0f0ef]">Add to Claude Desktop</span>
            </div>

            <div className="bg-[#f5f5f4] dark:bg-[#252525] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[8px] p-3.5 mb-3">
              <div className="text-[12px] font-medium text-[#475569] dark:text-[#aaa] mb-2">Option A — OAuth (recommended)</div>
              <p className="text-[11px] text-[#64748b] dark:text-[#666] leading-relaxed mb-2">In Claude Desktop: Settings → MCP Servers → Add custom connector</p>
              <div className="text-[11px] font-mono text-[#475569] dark:text-[#aaa] leading-[1.8]">
                <div>Server URL: <span className="text-[#1a1a1a] dark:text-[#f0f0ef]">https://mcp.skilflow.ai/mcp</span></div>
                <div>Auth URL: <span className="text-[#1a1a1a] dark:text-[#f0f0ef]">https://mcp.skilflow.ai/oauth/authorize</span></div>
                <div>Token URL: <span className="text-[#1a1a1a] dark:text-[#f0f0ef]">https://mcp.skilflow.ai/oauth/token</span></div>
                <div>Client ID: <span className="text-[#1a1a1a] dark:text-[#f0f0ef]">{oauthClientId || 'generate above'}</span></div>
                <div>Client Secret: <span className="text-[#1a1a1a] dark:text-[#f0f0ef]">{oauthSecretFull || oauthSecret || 'generate above'}</span></div>
              </div>
            </div>

            <div className="bg-[#f5f5f4] dark:bg-[#252525] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[8px] p-3.5 mb-3">
              <div className="text-[12px] font-medium text-[#475569] dark:text-[#aaa] mb-2">Option B — Config file (manual)</div>
              <div className="relative">
                <pre className="text-[11px] font-mono text-[#475569] dark:text-[#aaa] leading-relaxed whitespace-pre-wrap">{`{
  "mcpServers": {
    "skilflow": {
      "url": "https://mcp.skilflow.ai/mcp",
      "headers": {
        "Authorization": "Bearer ${mcpKeyFull || mcpKey || 'YOUR_API_KEY'}"
      }
    }
  }
}`}</pre>
                <button onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify({ mcpServers: { skilflow: { url: 'https://mcp.skilflow.ai/mcp', headers: { Authorization: `Bearer ${mcpKeyFull || mcpKey || 'YOUR_API_KEY'}` } } } }, null, 2))
                  setMcpConfigCopied(true); setTimeout(() => setMcpConfigCopied(false), 2000)
                }} className="absolute top-1 right-1 px-2 py-0.5 border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[4px] text-[10px] text-[#64748b] dark:text-[#666] bg-white dark:bg-[#1e1e1e] hover:bg-[#eee] dark:hover:bg-[#333] cursor-pointer"
                >{mcpConfigCopied ? '✓' : 'Copy'}</button>
              </div>
            </div>

            <div className="flex gap-2 mb-3">
              <button onClick={() => {
                const config = JSON.stringify({ mcpServers: { skilflow: { url: 'https://mcp.skilflow.ai/mcp', headers: { Authorization: `Bearer ${mcpKeyFull || mcpKey || 'YOUR_API_KEY'}` } } } }, null, 2)
                const blob = new Blob([config], { type: 'application/json' }); const url = URL.createObjectURL(blob)
                const a = document.createElement('a'); a.href = url; a.download = 'claude_desktop_config.json'; a.click(); URL.revokeObjectURL(url)
                showToast('Config file downloaded')
              }} className="px-3 py-1.5 border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] text-[12px] font-medium text-[#475569] dark:text-[#aaa] bg-transparent hover:bg-[#f5f5f4] dark:hover:bg-[#252525] cursor-pointer flex items-center gap-1.5">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download config file
              </button>
            </div>

            <div className="bg-[#f5f5f4] dark:bg-[#252525] border border-[#e5e5e3] dark:border-[#2e2e2e] rounded-[6px] px-3 py-2">
              <div className="text-[10px] font-semibold text-[#64748b] dark:text-[#555] uppercase tracking-[0.5px] mb-1">Config file location</div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[10px] font-semibold text-[#64748b] dark:text-[#555] uppercase tracking-[0.5px] w-6">Mac</span>
                <code className="text-[11px] font-mono text-[#475569] dark:text-[#aaa]">~/Library/Application Support/Claude/claude_desktop_config.json</code>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-[#64748b] dark:text-[#555] uppercase tracking-[0.5px] w-6">Win</span>
                <code className="text-[11px] font-mono text-[#475569] dark:text-[#aaa]">%APPDATA%\Claude\claude_desktop_config.json</code>
              </div>
            </div>
          </div>

          <div className="h-px bg-[#f0f0ef] dark:bg-[#2e2e2e] mb-5" />

          {/* Step 3 — Test */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-[22px] h-[22px] rounded-full bg-[#1a1a1a] dark:bg-[#f0f0ef] flex items-center justify-center text-[11px] font-bold text-white dark:text-[#1a1a1a] flex-shrink-0">3</div>
              <span className="text-[13px] font-medium text-[#1a1a1a] dark:text-[#f0f0ef]">Test & restart Claude Desktop</span>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={async () => {
                setMcpTestState('testing')
                try { const r = await testMCPConnection(); setMcpTestState(r.data.ok ? 'ok' : 'fail') }
                catch { setMcpTestState('fail') }
                setTimeout(() => setMcpTestState('idle'), 4000)
              }} disabled={mcpTestState === 'testing'}
                className={`px-3 py-1.5 border rounded-[6px] text-[12px] font-medium cursor-pointer disabled:opacity-50 ${
                  mcpTestState === 'ok' ? 'text-[#16a34a] border-[#bbf7d0] bg-[#f0fdf4] dark:bg-[#0f2a1a] dark:border-[#166534] dark:text-[#4ade80]'
                  : mcpTestState === 'fail' ? 'text-[#e53e3e] border-[#fecaca] bg-[#fff5f5] dark:bg-[#2a1010] dark:border-[#991b1b] dark:text-[#f87171]'
                  : 'text-[#475569] dark:text-[#aaa] border-[#e5e5e3] dark:border-[#2e2e2e] bg-transparent hover:bg-[#f5f5f4] dark:hover:bg-[#252525]'
                }`}
              >{mcpTestState === 'testing' ? 'Testing…' : mcpTestState === 'ok' ? '✓ MCP server reachable' : mcpTestState === 'fail' ? '✗ Connection failed' : 'Test connection'}</button>
            </div>
            <p className="text-[11px] text-[#64748b] dark:text-[#444] mt-3 leading-relaxed">
              After saving, restart Claude Desktop. Then try:<br />
              <span className="text-[#475569] dark:text-[#aaa]">"List my skills"</span> · <span className="text-[#475569] dark:text-[#aaa]">"Show me my tone skill"</span> · <span className="text-[#475569] dark:text-[#aaa]">"Save this as a skill called my-tone"</span>
            </p>
          </div>
        </Section>}

        {/* Placeholder tabs */}
        {activeTab === 'preferences' && (
          <div>
            <div className="mb-8">
              <div className="text-[13px] font-bold mb-1" style={{ color: 'var(--text1)' }}>Appearance</div>
              <div className="text-[12px] mb-4" style={{ color: 'var(--text2)' }}>Choose your preferred theme.</div>
              <ThemeSelector />
            </div>
            <div className="mb-8">
              <div className="text-[13px] font-bold mb-1" style={{ color: 'var(--text1)' }}>Onboarding</div>
              <div className="text-[12px] mb-4" style={{ color: 'var(--text2)' }}>Manage the guided onboarding experience.</div>
              <PrefToggle label="Show getting started checklist" desc="Display the progress card on the dashboard" on={prefs.show_checklist} onToggle={() => togglePref('show_checklist')} />
              <SettingsRow label="Spotlight guided tour" desc="Step-by-step tour with highlighted UI elements">
                <button onClick={() => navigate('/onboarding')} className="px-4 py-2 rounded-lg text-[13px] font-semibold cursor-pointer"
                  style={{ border: '1px solid var(--border)', color: 'var(--text2)', background: 'transparent', fontFamily: 'inherit' }}>
                  Restart tour
                </button>
              </SettingsRow>
              {/* Restart tour card */}
              <div className="flex items-center gap-3.5 rounded-[10px] p-4 mt-4" style={{ background: 'var(--brand-dim)', border: '1px solid rgba(124,58,237,0.25)' }}>
                <div className="w-10 h-10 rounded-[10px] flex items-center justify-center flex-shrink-0" style={{ background: 'var(--brand)' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                </div>
                <div className="flex-1">
                  <div className="text-[13px] font-semibold mb-0.5" style={{ color: 'var(--brand-light)' }}>Restart the guided tour</div>
                  <div className="text-[12px]" style={{ color: 'var(--text2)' }}>Walk through Skilflow's key features. Takes about 2 minutes.</div>
                </div>
                <button onClick={() => navigate('/onboarding')} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white cursor-pointer border-none"
                  style={{ background: 'var(--brand)', fontFamily: 'inherit' }}>
                  Start tour
                </button>
              </div>
            </div>
            <div className="mb-8">
              <div className="text-[13px] font-bold mb-1" style={{ color: 'var(--text1)' }}>Editor</div>
              <div className="text-[12px] mb-4" style={{ color: 'var(--text2)' }}>Configure the skill editor experience.</div>
              <PrefToggle label="AI inline suggestions" desc="Show AI tooltip when clicking a skill section" on={prefs.ai_suggestions} onToggle={() => togglePref('ai_suggestions')} />
              <PrefToggle label="Auto-save" desc="Save changes automatically while editing" on={prefs.auto_save} onToggle={() => togglePref('auto_save')} />
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div>
            <PrefToggle label="Skill published" desc="When a skill is promoted to production" on={prefs.notif_published} onToggle={() => togglePref('notif_published')} />
            <PrefToggle label="New comment" desc="When someone comments on your skill" on={prefs.notif_comments} onToggle={() => togglePref('notif_comments')} />
            <PrefToggle label="Skill conflict detected" desc="When two skills have conflicting instructions" on={prefs.notif_conflicts} onToggle={() => togglePref('notif_conflicts')} />
            <PrefToggle
              label="What's New notifications"
              desc="Auto-show the What's New panel when there are updates. You can always open it manually from the dashboard."
              on={!prefs.whats_new_muted}
              onToggle={() => togglePref('whats_new_muted')}
            />
          </div>
        )}

        {activeTab === 'organizations' && (
          <div>
            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ fontSize: 13, color: 'var(--text2)' }}>{orgs.length} organization{orgs.length !== 1 ? 's' : ''}</span>
              <button onClick={() => { setShowNewOrg(true); setNewOrgName(''); setNewOrgSlug('') }}
                style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, border: '1px solid var(--brand)', background: 'var(--brand)', cursor: 'pointer', color: '#000', fontFamily: 'inherit' }}>
                + New organization
              </button>
            </div>

            {/* Org list */}
            {orgsLoading ? (
              <div style={{ fontSize: 13, color: 'var(--text3)' }}>Loading…</div>
            ) : orgs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', border: '1.5px dashed var(--border)', borderRadius: 10, color: 'var(--text3)' }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>🏢</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>No organizations yet</div>
                <div style={{ fontSize: 12 }}>Create one to collaborate with others</div>
              </div>
            ) : orgs.map(org => {
              const isSelected = selectedOrg?.id === org.id
              return (
                <div key={org.id} style={{ background: 'var(--surface)', border: `1px solid ${isSelected ? 'var(--brand)' : 'var(--border)'}`, borderRadius: 10, marginBottom: 10, overflow: 'hidden', transition: 'border-color 0.15s' }}>
                  {/* Org header */}
                  <div onClick={() => isSelected ? setSelectedOrg(null) : loadOrgDetail(org)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', cursor: 'pointer' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(129,44,196,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--purple)' }}>{org.name[0].toUpperCase()}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text1)' }}>{org.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
                        {org.member_count} member{org.member_count !== 1 ? 's' : ''} · /{org.slug}
                      </div>
                    </div>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 8, textTransform: 'uppercase' as const, letterSpacing: '0.05em', background: org.your_role === 'owner' ? 'rgba(129,44,196,0.12)' : 'rgba(107,114,128,0.1)', color: org.your_role === 'owner' ? '#812cc4' : '#4b5563' }}>{org.your_role}</span>
                    <span style={{ fontSize: 16, color: 'var(--text3)', marginLeft: 4 }}>{isSelected ? '▲' : '▼'}</span>
                  </div>

                  {/* Expanded org detail */}
                  {isSelected && (
                    <div style={{ borderTop: '1px solid var(--border)', padding: '16px' }}>
                      {orgDetailLoading ? (
                        <div style={{ fontSize: 13, color: 'var(--text3)', padding: '8px 0' }}>Loading…</div>
                      ) : (
                        <>
                          {/* Members section */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase' as const, letterSpacing: '0.07em' }}>Members ({orgMembers.length})</span>
                            {(org.your_role === 'owner' || org.your_role === 'admin') && (
                              <button onClick={() => { setShowOrgInvite(true); setInvOrgEmail(''); setInvOrgRole('member') }}
                                style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', color: 'var(--text2)', fontFamily: 'inherit' }}>
                                + Invite member
                              </button>
                            )}
                          </div>
                          {orgMembers.map(m => {
                            const name = memberName(m) || m.user_id
                            const isOwner = m.role === 'owner'
                            return (
                              <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--purple)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                                  {memberInitials(m)}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                                  {m.email && !isPendingMember(m) && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{m.email}</div>}
                                </div>
                                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 8, textTransform: 'uppercase' as const, background: isOwner ? 'rgba(129,44,196,0.12)' : 'rgba(107,114,128,0.1)', color: isOwner ? '#812cc4' : '#4b5563' }}>{m.role}</span>
                                {!isOwner && (org.your_role === 'owner' || org.your_role === 'admin') && (
                                  <button onClick={async () => {
                                    setRemoveConfirmMember(m)
                                    setRemoveImpactCount(null)
                                    setRemoveImpactLoading(true)
                                    try {
                                      const data = await getRemovalImpact(org.id, m.user_id)
                                      setRemoveImpactCount(data.project_count ?? 0)
                                    } catch { setRemoveImpactCount(0) }
                                    finally { setRemoveImpactLoading(false) }
                                  }}
                                    style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 5, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', color: 'var(--text3)', fontFamily: 'inherit' }}
                                    onMouseEnter={e => { e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = 'rgba(220,38,38,0.3)' }}
                                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.borderColor = 'var(--border)' }}>
                                    Remove
                                  </button>
                                )}
                              </div>
                            )
                          })}

                          {/* Pending invites */}
                          {orgInvitations.length > 0 && (
                            <>
                              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase' as const, letterSpacing: '0.07em', margin: '16px 0 10px' }}>
                                Pending ({orgInvitations.length})
                              </div>
                              {orgInvitations.map(inv => (
                                <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--surface2)', color: 'var(--text3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                                    {inv.email[0]?.toUpperCase() ?? '?'}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, color: 'var(--text1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.email}</div>
                                  </div>
                                  <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 8, textTransform: 'uppercase' as const, background: 'rgba(253,183,20,0.1)', color: '#b38000' }}>Pending</span>
                                  <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 8, textTransform: 'uppercase' as const, background: 'rgba(107,114,128,0.1)', color: '#4b5563' }}>{inv.role}</span>
                                  <button onClick={() => handleCancelInvite(inv)}
                                    style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', color: 'var(--text3)', fontFamily: 'inherit' }}
                                    onMouseEnter={e => { e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = 'rgba(220,38,38,0.3)' }}
                                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.borderColor = 'var(--border)' }}>
                                    Cancel
                                  </button>
                                </div>
                              ))}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
        {activeTab === 'billing' && <div className="text-[13px]" style={{ color: 'var(--text2)' }}>Billing management coming soon.</div>}
        {activeTab === 'advanced' && (
          <div>
            <SettingsRow label="Reset onboarding" desc="Clear all onboarding progress and start fresh">
              <button onClick={() => navigate('/onboarding')} className="px-4 py-2 rounded-lg text-[13px] font-semibold cursor-pointer" style={{ border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', background: 'transparent', fontFamily: 'inherit' }}>Reset &amp; restart</button>
            </SettingsRow>
          </div>
        )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-[8px] text-[12px] font-medium z-50 whitespace-nowrap shadow-lg transition-all duration-200 animate-[slideUp_0.2s_ease]`}
          style={{ background: toastType === 'error' ? '#7f1d1d' : '#1a1a1a', color: toastType === 'error' ? '#fca5a5' : '#fff' }}>
          {toast}
        </div>
      )}

      {/* New Organization modal */}
      {showNewOrg && (
        <div className="modal-overlay" onClick={() => setShowNewOrg(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--text1)', marginBottom: 18 }}>New organization</div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Name *</label>
              <input autoFocus value={newOrgName}
                onChange={e => { setNewOrgName(e.target.value); setNewOrgSlug(slugify(e.target.value)) }}
                placeholder="Mitel Marketing"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text1)', outline: 'none', background: 'var(--surface)', boxSizing: 'border-box' as const }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>URL slug</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0, border: '1.5px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <span style={{ padding: '8px 10px', background: 'var(--surface2)', color: 'var(--text3)', fontSize: 12, borderRight: '1px solid var(--border)', flexShrink: 0 }}>skilflow.ai/org/</span>
                <input value={newOrgSlug} onChange={e => setNewOrgSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="mitel-marketing"
                  style={{ flex: 1, padding: '8px 12px', border: 'none', fontSize: 13, fontFamily: 'inherit', color: 'var(--text1)', outline: 'none', background: 'var(--surface)' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowNewOrg(false)} style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid var(--border)', cursor: 'pointer', background: 'transparent', fontFamily: 'inherit', color: 'var(--text2)' }}>Cancel</button>
              <button onClick={handleCreateOrg} disabled={newOrgSaving || !newOrgName.trim()}
                style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid var(--brand)', cursor: 'pointer', background: 'var(--brand)', fontFamily: 'inherit', color: '#000', opacity: newOrgSaving || !newOrgName.trim() ? 0.5 : 1 }}>
                {newOrgSaving ? 'Creating…' : 'Create organization'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Member to Org modal */}
      {showOrgInvite && selectedOrg && (
        <div className="modal-overlay" onClick={() => setShowOrgInvite(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--text1)', marginBottom: 4 }}>Invite to {selectedOrg.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 18 }}>An email invitation will be sent via Resend.</div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Email address *</label>
              <input autoFocus type="email" value={invOrgEmail} onChange={e => setInvOrgEmail(e.target.value)}
                placeholder="colleague@company.com"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text1)', outline: 'none', background: 'var(--surface)', boxSizing: 'border-box' as const }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Role</label>
              <select value={invOrgRole} onChange={e => setInvOrgRole(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text1)', outline: 'none', background: 'var(--surface)', cursor: 'pointer' }}>
                <option value="member">Member — can view org projects</option>
                <option value="admin">Admin — can invite/remove members</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowOrgInvite(false)} style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid var(--border)', cursor: 'pointer', background: 'transparent', fontFamily: 'inherit', color: 'var(--text2)' }}>Cancel</button>
              <button onClick={handleInviteOrgMember} disabled={invOrgSaving || !invOrgEmail.trim()}
                style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid var(--brand)', cursor: 'pointer', background: 'var(--brand)', fontFamily: 'inherit', color: '#000', opacity: invOrgSaving || !invOrgEmail.trim() ? 0.5 : 1 }}>
                {invOrgSaving ? 'Sending…' : 'Send invite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member confirm modal */}
      {removeConfirmMember && selectedOrg && (
        <div className="modal-overlay" onClick={() => { setRemoveConfirmMember(null); setRemoveImpactCount(null) }}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--text1)', marginBottom: 12 }}>Remove member?</div>
            <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7, marginBottom: 20 }}>
              Remove <strong style={{ color: 'var(--text1)' }}>{memberName(removeConfirmMember)}</strong> from <strong style={{ color: 'var(--text1)' }}>{selectedOrg.name}</strong>?
              {' '}They will lose access to all{' '}
              {removeImpactLoading
                ? <span style={{ color: 'var(--text3)' }}>…</span>
                : <strong style={{ color: '#dc2626' }}>{removeImpactCount ?? 0} project{(removeImpactCount ?? 0) !== 1 ? 's' : ''}</strong>
              }
              {' '}in this org.
              {' '}Their personal projects and any content they've created are unaffected.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setRemoveConfirmMember(null); setRemoveImpactCount(null) }}
                style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid var(--border)', cursor: 'pointer', background: 'transparent', fontFamily: 'inherit', color: 'var(--text2)' }}>
                Cancel
              </button>
              <button onClick={() => handleRemoveMember(removeConfirmMember)}
                disabled={removeImpactLoading}
                style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: removeImpactLoading ? 'not-allowed' : 'pointer', background: '#dc2626', fontFamily: 'inherit', color: '#fff', opacity: removeImpactLoading ? 0.5 : 1 }}>
                Remove member
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GitHub disconnect modal */}
      {showGhDisconnect && (
        <div className="modal-overlay" onClick={() => setShowGhDisconnect(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Disconnect GitHub</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20, lineHeight: 1.5 }}>
              Disconnect <strong style={{ color: 'var(--text1)' }}>{gh.repo}</strong>? This won't delete your skills from Supabase.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn-ghost" onClick={() => setShowGhDisconnect(false)} style={{ fontSize: 12, padding: '7px 14px' }}>Cancel</button>
              <button onClick={async () => {
                try {
                  await deleteConnection('github')
                  setGh({ repo: '', branch: 'main', token: '' })
                  localStorage.removeItem('skilflow_github')
                  setShowGhDisconnect(false)
                  setToast('GitHub disconnected'); setToastType('ok')
                } catch { setToast('Failed to disconnect'); setToastType('error') }
              }} style={{ padding: '7px 14px', fontSize: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#ef4444', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function NavSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.8px] px-2 mb-1.5 mt-4" style={{ color: 'var(--text3)' }}>{label}</div>
      {children}
    </div>
  )
}

function NavItem({ tab, active, onClick, icon, danger, children }: {
  tab: string; active: string; onClick: (t: any) => void; icon: React.ReactNode; danger?: boolean; children: React.ReactNode
}) {
  const isActive = active === tab
  return (
    <button onClick={() => onClick(tab)}
      className="flex items-center gap-2 w-full text-left px-2 py-[8px] rounded-lg text-[13px] font-medium cursor-pointer border-none mb-0.5"
      style={{
        background: isActive ? 'var(--brand-dim)' : 'transparent',
        color: isActive ? 'var(--brand-light)' : danger ? '#ef4444' : 'var(--text2)',
        fontFamily: 'inherit',
      }}>
      {icon}{children}
    </button>
  )
}

function SettingsRow({ label, desc, children }: {
  label: string; desc: string; children?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between py-[14px] gap-4" style={{ borderBottom: '1px solid var(--border)' }}>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium mb-0.5" style={{ color: 'var(--text1)' }}>{label}</div>
        <div className="text-[12px] leading-[1.4]" style={{ color: 'var(--text2)' }}>{desc}</div>
      </div>
      {children}
    </div>
  )
}

function ThemeSelector() {
  const [mode, setMode] = useState<string>(() => localStorage.getItem('skilflow_theme') || 'system')
  const apply = (m: string) => {
    setMode(m)
    localStorage.setItem('skilflow_theme', m)
    if (m === 'system') {
      const os = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      document.documentElement.setAttribute('data-theme', os)
    } else {
      document.documentElement.setAttribute('data-theme', m)
    }
  }
  const options = [
    { value: 'light', label: 'Light', icon: '\u2600' },
    { value: 'dark', label: 'Dark', icon: '\u263e' },
    { value: 'system', label: 'Use device setting', icon: '\ud83d\udcbb' },
  ]
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {options.map(o => (
        <button key={o.value} onClick={() => apply(o.value)}
          style={{
            flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
            background: mode === o.value ? 'var(--brand-dim)' : 'var(--surface2)',
            border: `1px solid ${mode === o.value ? 'rgba(124,58,237,0.3)' : 'var(--border)'}`,
            color: mode === o.value ? 'var(--brand-light)' : 'var(--text2)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, transition: 'all 0.15s',
          }}>
          <span style={{ fontSize: 18 }}>{o.icon}</span>
          <span style={{ fontSize: 11, fontWeight: 600 }}>{o.label}</span>
        </button>
      ))}
    </div>
  )
}

function PrefToggle({ label, desc, on, onToggle }: {
  label: string; desc: string; on: boolean; onToggle: () => void
}) {
  return (
    <div className="flex items-center justify-between py-[14px] gap-4" style={{ borderBottom: '1px solid var(--border)' }}>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium mb-0.5" style={{ color: 'var(--text1)' }}>{label}</div>
        <div className="text-[12px] leading-[1.4]" style={{ color: 'var(--text2)' }}>{desc}</div>
      </div>
      <button onClick={onToggle}
        className="w-9 h-5 rounded-[10px] relative cursor-pointer border-none flex-shrink-0 transition-colors duration-200"
        style={{ background: on ? 'var(--brand)' : 'var(--border2)' }}>
        <div className="w-4 h-4 rounded-full bg-white absolute top-[2px] transition-transform duration-200" style={{ left: '2px', transform: on ? 'translateX(16px)' : 'none' }} />
      </button>
    </div>
  )
}
